import React, { useState, useCallback, ComponentType } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { useFeedOptimization } from '@/utils/feedOptimization';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface LazyFeatureLoaderProps<T = any> {
  children: React.ReactNode;
  loadingFallback?: React.ReactNode;
  connectionBanner?: boolean;
  featureName?: string;
  loader: () => Promise<{ default: ComponentType<T> }>;
  componentProps: T;
  onLoad?: () => void;
}

export const LazyFeatureLoader = <T extends Record<string, any>>({
  children,
  loadingFallback,
  connectionBanner = true,
  featureName = 'feature',
  loader,
  componentProps,
  onLoad
}: LazyFeatureLoaderProps<T>) => {
  const [isLoading, setIsLoading] = useState(false);
  const [LoadedComponent, setLoadedComponent] = useState<ComponentType<T> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showManualOverride, setShowManualOverride] = useState(false);
  
  const { enableQuickActions } = useFeedOptimization();
  const connectionMode = useConnectionMode();
  
  const isOfflineOrSlow = connectionMode === 'offline' || connectionMode === 'lite';
  const shouldShowBanner = connectionBanner && isOfflineOrSlow && !LoadedComponent;

  const handleLoad = useCallback(async (forceLoad = false) => {
    if (LoadedComponent || isLoading) return;
    
    // Show override option for slow connections
    if (isOfflineOrSlow && !forceLoad && !showManualOverride) {
      setShowManualOverride(true);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    
    try {
      const module = await loader();
      setLoadedComponent(() => module.default);
      setShowManualOverride(false);
      onLoad?.();
    } catch (error) {
      console.error(`Failed to load ${featureName}:`, error);
      setLoadError(`Failed to load ${featureName}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  }, [LoadedComponent, isLoading, loader, featureName, isOfflineOrSlow, showManualOverride, onLoad]);

  // Auto-load for full connection mode
  const handleClick = useCallback(() => {
    if (connectionMode === 'full') {
      handleLoad();
    } else {
      handleLoad(false); // This will show override option
    }
  }, [connectionMode, handleLoad]);

  // Force load with manual override
  const handleForceLoad = useCallback(() => {
    handleLoad(true);
  }, [handleLoad]);

  if (LoadedComponent) {
    return <LoadedComponent {...componentProps} />;
  }

  return (
    <div className="space-y-3">
      {/* Connection Banner */}
      {shouldShowBanner && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {connectionMode === 'offline' ? (
                  <WifiOff className="h-4 w-4" />
                ) : (
                  <Wifi className="h-4 w-4" />
                )}
                <span>
                  {connectionMode === 'offline' 
                    ? `${featureName} will load when connection improves`
                    : `Heavy content loads on slow connections`
                  }
                </span>
              </div>
              {showManualOverride && enableQuickActions && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleForceLoad}
                  className="text-orange-700 border-orange-300 hover:bg-orange-100"
                >
                  Load Anyway
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Error State */}
      {loadError && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <div className="flex items-center justify-between">
              <span>{loadError}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleLoad(true)}
                className="text-red-700 border-red-300 hover:bg-red-100"
              >
                Retry
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Feature Trigger Button */}
      <div onClick={handleClick} className="cursor-pointer">
        {children}
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-2 text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading {featureName}...</span>
          </div>
          {loadingFallback}
        </div>
      )}
    </div>
  );
};